import { INVASAO_JUNINA_VIDEOS } from '../../domain/usecases/invasaoJunina';

interface Props {
  isAtivo: boolean;
}

// ─── Paleta de cores das bandeirinhas ────────────────────────────────────────
const CORES_BANDEIRINHA = [
  '#ef4444', '#f97316', '#eab308',
  '#22c55e', '#3b82f6', '#a855f7',
  '#ec4899', '#ffffff',
];

// ─── Bandeirinha triangular com balanço ──────────────────────────────────────
function Bandeirinha({ cor, delay }: { cor: string; delay: number }) {
  return (
    <svg
      width="20" height="28" viewBox="0 0 20 28"
      className="shrink-0 animate-junina-balanco"
      style={{ animationDelay: `${delay}ms`, transformOrigin: 'top center' }}
    >
      <polygon points="0,0 20,0 10,28" fill={cor} opacity="0.95" />
    </svg>
  );
}

// ─── Varal de bandeirinhas ────────────────────────────────────────────────────
function Varal({ count, flip }: { count: number; flip?: boolean }) {
  return (
    <div className={`relative flex items-start justify-around w-full px-1 ${flip ? 'rotate-180' : ''}`}>
      {/* Cordão */}
      <div className="absolute top-[1px] left-0 right-0 h-[2px] bg-gradient-to-r from-yellow-800/40 via-yellow-600/70 to-yellow-800/40" />
      {Array.from({ length: count }, (_, i) => (
        <Bandeirinha
          key={i}
          cor={CORES_BANDEIRINHA[i % CORES_BANDEIRINHA.length]}
          delay={i * 80}
        />
      ))}
    </div>
  );
}

// ─── Fogueira SVG animada ─────────────────────────────────────────────────────
function Fogueira({ size = 64 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className="animate-junina-fogueira" style={{ transformOrigin: 'bottom center' }}>
      {/* Lenhas */}
      <rect x="18" y="50" width="28" height="5" rx="2" fill="#92400e" />
      <rect x="12" y="53" width="40" height="4" rx="2" fill="#78350f" />
      {/* Chama externa */}
      <ellipse cx="32" cy="40" rx="14" ry="18" fill="#f97316" opacity="0.9" />
      {/* Chama média */}
      <ellipse cx="32" cy="38" rx="9" ry="14" fill="#fbbf24" opacity="0.95" />
      {/* Chama interna */}
      <ellipse cx="32" cy="36" rx="5" ry="9" fill="#fef08a" />
      {/* Brasas */}
      <circle cx="24" cy="52" r="2" fill="#ef4444" opacity="0.8" />
      <circle cx="38" cy="52" r="1.5" fill="#f97316" opacity="0.7" />
    </svg>
  );
}

// ─── Estrela SVG twinkle ──────────────────────────────────────────────────────
function Estrela({ x, y, size, delay }: { x: number; y: number; size: number; delay: number }) {
  return (
    <div
      className="absolute animate-junina-twinkle text-yellow-200"
      style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${delay}ms`, fontSize: `${size}px` }}
    >
      ★
    </div>
  );
}

// ─── Confete caindo ───────────────────────────────────────────────────────────
function Confete({ left, color, delay, size }: { left: number; color: string; delay: number; size: number }) {
  return (
    <div
      className="absolute top-0 animate-junina-confete"
      style={{
        left: `${left}%`,
        animationDelay: `${delay}ms`,
        width: size,
        height: size * 1.6,
        backgroundColor: color,
        borderRadius: '2px',
        animationDuration: `${2500 + delay}ms`,
      }}
    />
  );
}

// ─── Balão subindo ────────────────────────────────────────────────────────────
function Balao({ x, color, delay, scale }: { x: number; color: string; delay: number; scale: number }) {
  return (
    <div
      className="absolute bottom-16 animate-junina-balao-subir"
      style={{ left: `${x}%`, animationDelay: `${delay}ms`, transform: `scale(${scale})`, animationDuration: `${6000 + delay * 2}ms` }}
    >
      <svg width="28" height="40" viewBox="0 0 28 40">
        <ellipse cx="14" cy="14" rx="13" ry="14" fill={color} opacity="0.9" />
        <line x1="14" y1="28" x2="14" y2="40" stroke={color} strokeWidth="1.5" opacity="0.7" />
      </svg>
    </div>
  );
}

// ─── Dados estáticos (gerados 1x, fora do render) ────────────────────────────
const ESTRELAS = [
  { x: 5,  y: 8,  size: 10, delay: 0   },
  { x: 15, y: 5,  size: 14, delay: 400 },
  { x: 28, y: 10, size: 8,  delay: 200 },
  { x: 42, y: 6,  size: 12, delay: 700 },
  { x: 58, y: 8,  size: 9,  delay: 100 },
  { x: 70, y: 5,  size: 13, delay: 500 },
  { x: 83, y: 9,  size: 8,  delay: 300 },
  { x: 93, y: 7,  size: 11, delay: 600 },
];

const CONFETES_DATA = [
  { left: 5,  color: '#ef4444', delay: 0,    size: 8  },
  { left: 12, color: '#fbbf24', delay: 200,  size: 7  },
  { left: 22, color: '#22c55e', delay: 600,  size: 9  },
  { left: 33, color: '#a855f7', delay: 100,  size: 6  },
  { left: 45, color: '#f97316', delay: 400,  size: 8  },
  { left: 55, color: '#3b82f6', delay: 800,  size: 7  },
  { left: 65, color: '#ec4899', delay: 300,  size: 9  },
  { left: 75, color: '#eab308', delay: 500,  size: 6  },
  { left: 85, color: '#ef4444', delay: 150,  size: 8  },
  { left: 92, color: '#22c55e', delay: 700,  size: 7  },
  { left: 18, color: '#a855f7', delay: 900,  size: 10 },
  { left: 48, color: '#fbbf24', delay: 1100, size: 6  },
  { left: 78, color: '#3b82f6', delay: 950,  size: 9  },
];

const BALOES_DATA = [
  { x: 4,  color: '#ef4444', delay: 0,    scale: 0.9 },
  { x: 10, color: '#fbbf24', delay: 800,  scale: 1.1 },
  { x: 82, color: '#22c55e', delay: 400,  scale: 1.0 },
  { x: 90, color: '#3b82f6', delay: 1200, scale: 0.85 },
  { x: 96, color: '#a855f7', delay: 200,  scale: 1.0 },
];

// ─── Componente principal ─────────────────────────────────────────────────────
export default function TvInvasaoJuninaTakeover({ isAtivo }: Props) {
  void INVASAO_JUNINA_VIDEOS;

  if (!isAtivo) return null;

  return (
    <div className="fixed inset-0 z-[55] pointer-events-none overflow-hidden">

      {/* ── Fundo: céu noturno de festa junina ── */}
      <div className="absolute inset-0 animate-junina-overlay-in bg-gradient-to-b from-[#0f0a1e] via-[#1a0a2e]/80 to-[#2d1200]/60" />

      {/* ── Estrelas ── */}
      {ESTRELAS.map((e, i) => <Estrela key={i} {...e} />)}

      {/* ── Confetes caindo ── */}
      {CONFETES_DATA.map((c, i) => <Confete key={i} {...c} />)}

      {/* ── Balões subindo ── */}
      {BALOES_DATA.map((b, i) => <Balao key={i} {...b} />)}

      {/* ── Varal superior (duplo) ── */}
      <div className="absolute top-0 left-0 right-0 flex flex-col gap-3 pt-1 animate-junina-varal-in">
        <Varal count={24} />
        <Varal count={24} />
      </div>

      {/* ── Varal inferior ── */}
      <div
        className="absolute bottom-0 left-0 right-0 flex flex-col gap-3 pb-1 animate-junina-varal-in"
        style={{ animationDelay: '100ms' }}
      >
        <Varal count={24} flip />
        <Varal count={24} flip />
      </div>

      {/* ── Fogueiras nos cantos ── */}
      <div className="absolute bottom-16 left-6 opacity-90">
        <Fogueira size={72} />
      </div>
      <div className="absolute bottom-16 right-6 opacity-90 scale-x-[-1]">
        <Fogueira size={72} />
      </div>

      {/* ── Badge central ── */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="animate-junina-badge-in flex flex-col items-center gap-3">

          {/* Chapéu de palha decorativo */}
          <div className="text-5xl" style={{ filter: 'drop-shadow(0 2px 8px rgba(234,179,8,0.6))' }}>
            🪗
          </div>

          {/* Título principal */}
          <div className="relative px-10 py-5 rounded-3xl text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(180,83,9,0.92) 0%, rgba(234,88,12,0.95) 40%, rgba(180,83,9,0.92) 100%)',
              boxShadow: '0 0 60px rgba(234,88,12,0.5), 0 8px 32px rgba(0,0,0,0.6), inset 0 1px 0 rgba(251,191,36,0.3)',
              border: '2px solid rgba(251,191,36,0.5)',
            }}
          >
            {/* Brilho interno */}
            <div className="absolute inset-0 rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent" />
            </div>

            <p
              className="relative font-black uppercase leading-none tracking-widest animate-junina-titulo-glow"
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                color: '#fef9c3',
                WebkitTextStroke: '1px rgba(180,83,9,0.4)',
              }}
            >
              🎉 Arraiá do Espetos! 🎉
            </p>

            <p
              className="relative mt-2 font-bold uppercase tracking-[0.25em] animate-junina-sub-in"
              style={{ fontSize: 'clamp(0.65rem, 1.5vw, 0.9rem)', color: '#fde68a', opacity: 0.9 }}
            >
              ✦ Invasão Junina ✦
            </p>
          </div>

          {/* Emojis de festa */}
          <div
            className="flex gap-3 text-2xl animate-junina-sub-in"
            style={{ animationDelay: '0.8s', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }}
          >
            <span>🌽</span>
            <span>🎆</span>
            <span>💃</span>
            <span>🎆</span>
            <span>🌽</span>
          </div>

        </div>
      </div>

    </div>
  );
}
